import {
  compareDates,
  formatWeight,
  parseWeight,
  weightNumber,
  type LocalDate,
  type WeightEntryLike,
  type WeightUnit,
} from '@tahi/core';
import { useEffect, useId, useState } from 'react';
import {
  deleteWeight,
  logWeight,
  restoreWeight,
  undoLogWeight,
} from '../../../data/commands/weight';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { copy, errorText } from '../copy';
import { dayLabel } from '../format';
import { FOCUS } from '../styles';

interface Props {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** The day the sheet opens on (today for a new log, the entry's day for an edit). */
  date: LocalDate;
  today: LocalDate;
  unit: WeightUnit;
  /** Every live entry, to prefill a day that has one and show the replace hint. */
  entries: readonly WeightEntryLike[];
  /** Prefilled when the day has no entry yet (the last weigh-in). */
  lastValueG: number | null;
}

/**
 * Log or edit a day's weight: today's date and the last value filled in (spec §6), a decimal
 * keypad on phones, and one entry per day (saving on a day that has one replaces it).
 */
export function LogSheet({ open, onOpenChange, date, today, unit, entries, lastValueG }: Props) {
  const env = useCommandEnv();
  const formId = useId();
  const [day, setDay] = useState(date);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const existing = entries.find((e) => e.date === day) ?? null;

  const fill = (d: LocalDate) => {
    const e = entries.find((x) => x.date === d);
    const g = e?.valueG ?? lastValueG;
    setText(g === null || g === undefined ? '' : weightNumber(g, unit));
    setNote(e?.note ?? '');
  };

  useEffect(() => {
    if (!open) return;
    setDay(date);
    fill(date);
    setError(null);
    // Reset only when the sheet opens or its day changes from outside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const save = async () => {
    const parsed = parseWeight(text, unit);
    if (!parsed.ok) return setError(errorText(parsed.error));
    if (compareDates(day, today) > 0) return setError(errorText('future_date'));
    const r = await logWeight(env, { date: day, valueG: parsed.grams, unit, note });
    if (!r.ok) return setError(errorText(r.error));
    onOpenChange(false);
    const w = formatWeight(r.entry.valueG, unit);
    showToast({
      message: r.replaced ? copy.updated(w) : copy.logged(w),
      action: { label: copy.undo, onPress: () => void undoLogWeight(env, day, r.replaced) },
    });
  };

  const remove = async () => {
    if (!existing) return;
    const r = await deleteWeight(env, existing.date);
    if (!r.ok) return setError(errorText(r.error));
    onOpenChange(false);
    showToast({
      message: copy.deleted,
      action: { label: copy.undo, onPress: () => void restoreWeight(env, existing) },
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={date === today && !existing ? copy.logTitle : copy.editTitle(dayLabel(day, today))}
      footer={
        <div className="flex gap-2">
          {existing ? (
            <Button variant="quiet-destructive" onClick={() => void remove()}>
              {copy.delete}
            </Button>
          ) : null}
          <Button variant="primary" block type="submit" form={formId}>
            {copy.save}
          </Button>
        </div>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={`${formId}-w`} className="text-sm font-medium text-muted">
            {copy.weight} ({unit})
          </label>
          <div className="flex items-baseline justify-center gap-2 py-2">
            <input
              id={`${formId}-w`}
              data-autofocus
              type="text"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="done"
              value={text}
              aria-invalid={error ? true : undefined}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              className={`h-16 w-40 rounded-2xl border border-border bg-surface text-center font-mono text-4xl tabular-nums text-fg ${FOCUS}`}
            />
            <span className="text-xl text-muted">{unit}</span>
          </div>
        </div>
        <TextField
          label={copy.date}
          type="date"
          max={today}
          value={day}
          onChange={(e) => {
            const d = e.target.value;
            if (!d) return;
            setDay(d);
            fill(d);
            setError(null);
          }}
        />
        <TextField
          label={copy.note}
          placeholder={copy.notePlaceholder}
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {existing ? (
          <p className="text-sm text-muted">
            {existing.unit !== unit
              ? `${copy.replaceHint} ${copy.enteredAs(formatWeight(existing.valueG, existing.unit))}.`
              : copy.replaceHint}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
}
